import type { KeycloakInstance } from 'keycloak-js';
import { defaultTo, isNull } from 'lodash-es';
import { log } from '../log';
import type { AccessTokenPayload } from '../tokens';
import { AuthenticationTokens } from '../tokens';
import { SessionEvents } from './session-events';
import type { User } from './user';
import { toUser } from './user';

export type Session = Pick<SessionImpl, keyof SessionImpl>;

export class SessionImpl {

  private events: SessionEvents;

  private tokens: AuthenticationTokens;

  constructor(
    private keycloakInstance: KeycloakInstance,
  ) {
    this.events = new SessionEvents(keycloakInstance);
    this.tokens = new AuthenticationTokens(keycloakInstance);
  }

  get isAuthenticated() {
    return defaultTo(this.keycloakInstance.authenticated, false);
  }

  getUser() {
    return toUser(
      this.tokens.idTokenPayload,
      this.tokens.accessTokenPayload,
    );
  }

  getAccessToken() {
    return this.tokens.accessToken;
  }

  getAccessTokenPayload() {
    return this.tokens.accessTokenPayload;
  }

  getIdTokenPayload() {
    return this.tokens.idTokenPayload;
  }

  getAuthServerUrl() {
    return this.keycloakInstance.authServerUrl;
  }

  refreshTokens() {
    return this.tokens.refresh();
  }

  onUserChange(listener: (user: User | null) => void) {
    const userChangeListener = () => {
      log.debug('Session event userChange fired', this.getUser());
      listener(this.getUser());
    };

    return this.events.on(
      [
        'authentication',
        'authenticationFailed',
        'logout',
      ],
      userChangeListener,
    );
  }

  onAccessTokenChange(listener: (accessToken: { accessToken: string; accessTokenPayload: AccessTokenPayload } | null) => void) {
    const accessTokenChangeListener = () => {
      log.debug('Session event accessTokenChange fired');
      const accessToken = this.getAccessToken();
      const accessTokenPayload = this.getAccessTokenPayload();

      if (isNull(accessToken) || isNull(accessTokenPayload)) {
        listener(null);
      } else {
        listener({ accessToken, accessTokenPayload });
      }
    };

    return this.events.on(
      [
        'tokenRefresh',
        'tokenRefreshFailed',
        'logout',
      ],
      accessTokenChangeListener,
    );
  }

  onLogout(listener: () => void) {
    const logoutListener = () => {
      log.debug('Session event logout fired');
      listener();
    };

    return this.events.on(
      [
        'logout',
      ],
      logoutListener,
    );
  }

  onAuthenticatedChange(listener: (isAuthenticated: boolean) => void) {
    const authenticatedChangeListener = () => {
      log.debug('Session event authenticatedChange fired', this.isAuthenticated);
      listener(this.isAuthenticated);
    };

    return this.events.on(
      [
        'authentication',
        'authenticationFailed',
        'tokenRefresh',
        'tokenRefreshFailed',
        'logout',
      ],
      authenticatedChangeListener,
    );
  }

  logout(): Promise<void> {
    return this.keycloakInstance.logout();
  }

  async destroy() {
    log.debug('Destroying session...');
    await this.tokens.destroy();
  }
}
